// @flow
import type { ChildrenArray } from 'react';
import React from 'react';
import {
  productColor,
  productColorSunflower,
  illustrationColor,
} from '../../designSystem.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { Text } from '../../Text.js';
import Latex from '../../Latex.js';
import { LatexTextAtom } from '../../LatexTextAtom.js';
import { List } from '../../List.js';
import Image from '../../Image.js';
import BoxAndWhiskerPlot from '../../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { NumberLine } from '../../NumberLine.js';
import { StemAndLeafPlot } from '../../StemAndLeafPlot.js';
import TabularData from '../../TabularData.js';
import { TwoColumnProof } from '../../TwoColumnProof.js';
import { AlgebraicWorking } from '../../AlgebraicWorking.js';
import DraftImage from '../../DraftImage.js';

import {
  DisabilityIcon,
  EnglishLanguageLearnerIcon,
  InstructionalSupportInformationIcon,
  CommonMisconceptionIcon,
} from '../../InstructionalSupportIcons.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { HollowCollapseChevron } from '../common-components/internal-components.js';

export type INVTNInstructionalSupportsChildren = ChildrenArray<
  | React$Element<typeof INVTNInstructionalSupportInformation>
  | React$Element<typeof INVTNEnglishLearnerSupport>
  | React$Element<typeof INVTNInstructionalSupportDisability>,
>;

export const INVTNInstructionalSupportsChildrenChecker = childrenArrayMatcher(
  {
    min: 0,
    max: 1,
    elementType: t.elementType(INVTNInstructionalSupportInformation),
  },
  {
    min: 0,
    max: 1,
    elementType: t.elementType(INVTNEnglishLearnerSupport),
  },
  {
    min: 0,
    max: 1,
    elementType: t.elementType(INVTNInstructionalSupportDisability),
  },
);

export function INVTNInstructionalSupports(props: {|
  children: INVTNInstructionalSupportsChildren,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInstructionalSupports,
  );
  if (failedValidation) return null;

  return <div style={{ margin: '24px 0' }}>{props.children}</div>;
}
INVTNInstructionalSupports.spec = {
  children: INVTNInstructionalSupportsChildrenChecker,
};

type INVTNInstructionalSupportChildrenArray = ChildrenArray<
  // Primitives
  | React$Element<typeof Text>
  | React$Element<typeof Latex>
  | React$Element<typeof LatexTextAtom>
  | React$Element<typeof List>
  | React$Element<typeof Image>
  | React$Element<typeof DraftImage>
  // probably only need those above this line
  | React$Element<typeof BoxAndWhiskerPlot>
  | React$Element<typeof CartesianPlane>
  | React$Element<typeof NumberLine>
  | React$Element<typeof StemAndLeafPlot>
  | React$Element<typeof TabularData>
  | React$Element<typeof TwoColumnProof>
  | React$Element<typeof AlgebraicWorking>,
>;

const INVTNInstructionalSupportChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    // Primitives
    t.elementType(Text),
    t.elementType(Latex),
    t.elementType(LatexTextAtom),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    // again, probably only need those above this line
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(TabularData),
    t.elementType(TwoColumnProof),
    t.elementType(AlgebraicWorking),
  ),
);

const INVTNInstructionalSupportSpec = {
  heading: t.string,
  children: INVTNInstructionalSupportChildrenChecker,
};

export function INVTNInstructionalSupportInformation(props: {|
  heading: string,
  children: INVTNInstructionalSupportChildrenArray,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInstructionalSupportInformation,
  );
  if (failedValidation) return null;

  return (
    <INVTNSupportCollapsibleInternal
      title="Targeted instructional strategies"
      icon={<InstructionalSupportInformationIcon />}
      heading={props.heading}
    >
      {props.children}
    </INVTNSupportCollapsibleInternal>
  );
}
INVTNInstructionalSupportInformation.spec = INVTNInstructionalSupportSpec;

export function INVTNEnglishLearnerSupport(props: {|
  heading: string,
  children: INVTNInstructionalSupportChildrenArray,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNEnglishLearnerSupport,
  );
  if (failedValidation) return null;

  return (
    <INVTNSupportCollapsibleInternal
      title="English language learner support"
      icon={<EnglishLanguageLearnerIcon />}
      heading={props.heading}
    >
      {props.children}
    </INVTNSupportCollapsibleInternal>
  );
}
INVTNEnglishLearnerSupport.spec = INVTNInstructionalSupportSpec;

export function INVTNInstructionalSupportDisability(props: {|
  heading: string,
  children: INVTNInstructionalSupportChildrenArray,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVTNInstructionalSupportDisability,
  );
  if (failedValidation) return null;

  return (
    <INVTNSupportCollapsibleInternal
      title="Student with disabilities support"
      icon={<DisabilityIcon />}
      heading={props.heading}
    >
      {props.children}
    </INVTNSupportCollapsibleInternal>
  );
}
INVTNInstructionalSupportDisability.spec = INVTNInstructionalSupportSpec;

export const INVTNSupportCollapsibleInternal = (props: {|
  openByDefault?: boolean,
  title: string,
  icon:
    | React$Element<typeof DisabilityIcon>
    | React$Element<typeof EnglishLanguageLearnerIcon>
    | React$Element<typeof CommonMisconceptionIcon>
    | React$Element<typeof InstructionalSupportInformationIcon>,
  heading: string,
  children: ChildrenArray<React$Element<any>>,
|}) => {
  const inPrintMode = usePrintMode();
  const [open, setOpen] = React.useState(props.openByDefault || inPrintMode);
  return (
    <div
      style={{
        border: `2px solid ${productColor.iron}`,
        borderRadius: 8,
        marginTop: 12,
        backgroundColor: 'auto',
      }}
    >
      <div style={{ breakInside: 'avoid' }}>
        <div
          style={{
            display: 'table',
            padding: 16,
            cursor: 'pointer',
          }}
          onClick={() => setOpen((prevState) => !prevState)}
        >
          <span style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            {props.icon}
          </span>
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              width: '100%',
            }}
          >
            <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
              <h1
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  margin: 0,
                  color: illustrationColor.pickledBluewood,
                }}
              >
                {props.heading}
              </h1>
              <p
                style={{
                  color: productColorSunflower.grey90,
                  fontSize: 16,
                  margin: 0,
                }}
              >
                {props.title}
              </p>
            </div>
          </div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            <HollowCollapseChevron isOpen={open} />
          </div>
        </div>
        <div
          style={{
            // When we are closed we still want children to be in the DOM
            // AND (critically) we want the children to exist in the box tree
            // as well, so that we can perform accurate layout measurements.
            // This combination of styles ensures children exist in the box
            // tree, but do not affect layout, nor are drawn to the screen.
            visibility: open ? 'visible' : 'hidden',
            height: open ? 'auto' : 0,
            lineHeight: '22px',
            fontWeight: 400,
            padding: open ? '0 16px 16px 16px' : '0 16px',
          }}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
};
